/**
 * AI 議題工作區 — 主題管理頁
 * 對應 8 步價值鏈 Step 4「主題：打算做甚麼」
 * func_code: ee_research_subjects
 *
 * 主題是議題上層的 actionable goal。一個主題下可有多議題（N:1）。
 * 議題可獨立存在或歸屬主題（subject_id nullable）。
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Stack, Button, TextField, Chip, IconButton, Tooltip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Dialog, DialogTitle, DialogContent, DialogActions, Switch, FormControlLabel,
  CircularProgress, Typography, MenuItem, Select, FormControl, InputLabel,
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  OpenInNew as OpenInNewIcon, Refresh as RefreshIcon,
  TrackChanges as TargetIcon, AutoAwesome as MagicIcon,
  Chat as ChatIcon,
} from '@mui/icons-material';

import FunctionPageHeader from '../components/FunctionPageHeader';
import SubjectOneClickDialog from '../components/research/SubjectOneClickDialog';
import SubjectConversationDialog from '../components/research/SubjectConversationDialog';
import { SubjectDraft } from '../types/research';
import { useMessage } from '../contexts/MessageContext';
import { logView, logCreate, logUpdate, logDelete } from '../utils/userLogHelper';
import { usePermission } from '../hooks/usePermission';
import { Subject, SubjectCreate, SubjectUpdate } from '../types/research';
import {
  listSubjects, createSubject, updateSubject, deleteSubject,
} from '../services/researchService';
import { formatLocal } from '../utils/dateHelper';

const FUNC_CODE = 'ee_research_subjects';

const fmt = (iso?: string | null) => iso ? formatLocal(iso) : '—';

interface FormData {
  subject_code: string;
  subject_name: string;
  description: string;
  status: 'active' | 'archived';
  is_active: boolean;
}

const emptyForm: FormData = {
  subject_code: '',
  subject_name: '',
  description: '',
  status: 'active',
  is_active: true,
};

const ResearchSubjectsPage: React.FC = () => {
  const navigate = useNavigate();
  const { showSuccess, showApiError } = useMessage();
  const { hasPermission } = usePermission();
  const canCreate = hasPermission(FUNC_CODE, 'create');
  const canUpdate = hasPermission(FUNC_CODE, 'update');
  const canDelete = hasPermission(FUNC_CODE, 'delete');

  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<Subject | null>(null);
  const [form, setForm] = useState<FormData>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [oneClickOpen, setOneClickOpen] = useState(false);
  const [conversationOpen, setConversationOpen] = useState(false);
  const [handoffDraft, setHandoffDraft] = useState<SubjectDraft | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listSubjects({ search: search || undefined });
      setSubjects(data);
      logView(FUNC_CODE, { search: search || undefined, count: data.length });
    } catch (err: any) {
      logView(FUNC_CODE, { search: search || undefined }, String(err?.message || err));
      showApiError(err);
    } finally {
      setLoading(false);
    }
  }, [search, showApiError]);

  useEffect(() => { reload(); }, [reload]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setEditOpen(true);
  };

  const openEdit = (s: Subject) => {
    setEditing(s);
    setForm({
      subject_code: s.subject_code,
      subject_name: s.subject_name,
      description: s.description || '',
      status: s.status,
      is_active: s.is_active,
    });
    setEditOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    const payload = {
      subject_code: form.subject_code.trim(),
      subject_name: form.subject_name.trim(),
      description: form.description || undefined,
      status: form.status,
      is_active: form.is_active,
    };
    try {
      if (editing) {
        const { subject_code, ...updateData } = payload;
        await updateSubject(editing.id, updateData as SubjectUpdate);
        logUpdate(FUNC_CODE, { ...editing }, { ...payload, id: editing.id });
        showSuccess('SYS020002', { name: form.subject_name, id: String(editing.id) });
      } else {
        const created = await createSubject(payload as SubjectCreate);
        logCreate(FUNC_CODE, { ...created });
        showSuccess('SYS020001', { name: form.subject_name });
      }
      setEditOpen(false);
      reload();
    } catch (err: any) {
      if (editing) {
        logUpdate(FUNC_CODE, { ...editing }, payload, String(err?.message || err));
      } else {
        logCreate(FUNC_CODE, payload, String(err?.message || err));
      }
      showApiError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (s: Subject) => {
    if (!window.confirm(
      `確定刪除主題「${s.subject_name}」？\n旗下 ${s.topic_count ?? 0} 個議題的歸屬會變空（議題本身不會被刪除）。`,
    )) return;
    try {
      await deleteSubject(s.id);
      logDelete(FUNC_CODE, { ...s });
      showSuccess('SYS020003', { name: s.subject_name, id: String(s.id) });
      reload();
    } catch (err: any) {
      logDelete(FUNC_CODE, { ...s }, String(err?.message || err));
      showApiError(err);
    }
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <FunctionPageHeader funcCode={FUNC_CODE} fallback="主題管理" />
        <Stack direction="row" spacing={1}>
          {canCreate && (
            <Button variant="contained" color="secondary"
                    startIcon={<MagicIcon />}
                    onClick={() => { setHandoffDraft(null); setOneClickOpen(true); }}>
              ✨ AI 一鍵生成
            </Button>
          )}
          {canCreate && (
            <Button variant="contained" color="info"
                    startIcon={<ChatIcon />}
                    onClick={() => setConversationOpen(true)}>
              ✨ AI 對話建立
            </Button>
          )}
          {canCreate && (
            <Button variant="outlined" startIcon={<AddIcon />} onClick={openCreate}>
              手動新增
            </Button>
          )}
        </Stack>
      </Stack>

      <Stack direction="row" spacing={1} sx={{ mb: 2 }} alignItems="center">
        <TextField
          size="small" placeholder="搜尋主題代碼或名稱"
          value={search} onChange={e => setSearch(e.target.value)}
          sx={{ minWidth: 280 }}
        />
        <Button
          size="small" variant="outlined"
          startIcon={loading ? <CircularProgress size={14} /> : <RefreshIcon />}
          onClick={reload} disabled={loading}
        >重新整理</Button>
      </Stack>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell width={120}>代碼</TableCell>
              <TableCell>名稱</TableCell>
              <TableCell width={100}>狀態</TableCell>
              <TableCell width={100} align="center">議題數</TableCell>
              <TableCell width={70}>啟用</TableCell>
              <TableCell width={170}>更新時間</TableCell>
              <TableCell width={140} align="right">操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {subjects.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  {loading ? '載入中…' : '尚無主題'}
                </TableCell>
              </TableRow>
            ) : (
              subjects.map(s => (
                <TableRow key={s.id} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {s.subject_code}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      <TargetIcon fontSize="small" sx={{ verticalAlign: 'text-bottom', mr: 0.5, color: 'primary.main' }} />
                      {s.subject_name}
                    </Typography>
                    {s.description && (
                      <Typography variant="caption" color="text.secondary"
                                  sx={{ display: 'block' }}>
                        {s.description}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={s.status === 'active' ? '進行中' : '已封存'}
                      size="small"
                      color={s.status === 'active' ? 'primary' : 'default'}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="點擊查看旗下議題">
                      <Chip
                        size="small"
                        label={s.topic_count ?? 0}
                        clickable
                        onClick={() => navigate(`/ee_research_topics?subject_id=${s.id}`)}
                        color={s.topic_count && s.topic_count > 0 ? 'success' : 'default'}
                        variant={s.topic_count && s.topic_count > 0 ? 'filled' : 'outlined'}
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={s.is_active ? '是' : '否'}
                      size="small"
                      color={s.is_active ? 'primary' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">{fmt(s.updated_at)}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="進入主題（旗下議題 + 主題素材庫）">
                      <IconButton size="small" color="primary"
                                  onClick={() => navigate(`/ee_research_subjects/${s.id}`)}>
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {canUpdate && (
                      <Tooltip title="編輯">
                        <IconButton size="small" onClick={() => openEdit(s)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {canDelete && (
                      <Tooltip title="刪除">
                        <IconButton size="small" color="error" onClick={() => handleDelete(s)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* 新增 / 編輯主題 */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? '編輯主題' : '新增主題'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="主題代碼" required size="small"
              value={form.subject_code}
              onChange={e => setForm({ ...form, subject_code: e.target.value })}
              disabled={!!editing}
              helperText="英數底線；建立後不可改"
            />
            <TextField
              label="主題名稱（actionable goal）" required size="small"
              value={form.subject_name}
              onChange={e => setForm({ ...form, subject_name: e.target.value })}
              helperText='例：「爭取投資方提供投資意願」'
            />
            <TextField
              label="說明（要達成什麼）" multiline rows={3} size="small"
              value={form.description}
              onChange={e => setForm({ ...form, description: e.target.value })}
            />
            <FormControl size="small">
              <InputLabel>狀態</InputLabel>
              <Select
                label="狀態" value={form.status}
                onChange={e => setForm({ ...form, status: e.target.value as 'active' | 'archived' })}
              >
                <MenuItem value="active">進行中</MenuItem>
                <MenuItem value="archived">已封存</MenuItem>
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={form.is_active}
                  onChange={e => setForm({ ...form, is_active: e.target.checked })}
                />
              }
              label="啟用"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>取消</Button>
          <Button onClick={handleSave} variant="contained"
                  disabled={saving || !form.subject_code || !form.subject_name}>
            {saving ? <CircularProgress size={18} /> : '儲存'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ✨ AI 一鍵生成主題（Mode B）— 系統存在的意義 */}
      <SubjectOneClickDialog
        open={oneClickOpen}
        initialDraft={handoffDraft}
        onClose={() => { setOneClickOpen(false); setHandoffDraft(null); }}
        onCreated={(subjectId) => {
          setOneClickOpen(false);
          setHandoffDraft(null);
          reload();
          navigate(`/ee_research_subjects/${subjectId}`);
        }}
      />

      {/* ✨ AI 對話建立主題（Mode A）— 透過問答式達成一鍵生成 */}
      <SubjectConversationDialog
        open={conversationOpen}
        onClose={() => setConversationOpen(false)}
        onReady={(draft) => {
          // 對話完成 → 把 draft handoff 給 OneClickDialog 走 preview / 採納
          setConversationOpen(false);
          setHandoffDraft(draft);
          setOneClickOpen(true);
        }}
      />
    </Box>
  );
};

export default ResearchSubjectsPage;